import React, { useId, useState } from 'react';
import { Alert, Box, Button, ButtonBase, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { useZoomPan } from '../../../hooks/useZoomPan';
import { articleCrackImages } from './articleImages';
import { colors } from './design';
import { PublicLocale } from './routes';

const copy = {
  sv: {
    previewAlt: 'Portalvy med en markerad spricka vid takfoten',
    open: 'Förstora sprickan',
    caption: 'Portalvy med en AI-markerad spricka. Procenttalen anger modellens säkerhet i identifieringen, inte skadans allvar eller åtgärdsprioritet.',
    previewError: 'Portalbilden kunde inte laddas. Du kan fortfarande öppna detaljbilden.',
    title: 'Sprickan i originalfotot',
    detailAlt: 'Beskuren detalj från originalfotot med sprickan vid takfoten',
    instructions: 'Detalj från originalfotot, utan portalens markeringar. Använd zoomknapparna och dra i bilden. Med tangentbord: +/− för zoom, piltangenter för förflyttning, 0 för återställning och Escape för att stänga.',
    close: 'Stäng detaljbild',
    zoomIn: 'Zooma in',
    zoomOut: 'Zooma ut',
    reset: 'Återställ vy',
    viewport: 'Zoombart foto av sprickan',
    zoom: 'Zoomnivå',
    loading: 'Laddar detaljbild…',
    error: 'Detaljbilden kunde inte laddas.',
    retry: 'Försök igen',
  },
  en: {
    previewAlt: 'Portal view with a marked crack at the roofline',
    open: 'Enlarge the crack detail',
    caption: 'Portal view with an AI-marked crack. Percentages indicate model confidence in the identification, not defect severity or repair priority.',
    previewError: 'The portal image could not be loaded. You can still open the detail photograph.',
    title: 'Crack detail from the original photograph',
    detailAlt: 'Crop from the original photograph showing the crack at the roofline',
    instructions: 'Detail from the original photograph, without portal annotations. Use the zoom buttons and drag the image. Keyboard: +/− to zoom, arrow keys to pan, 0 to reset and Escape to close.',
    close: 'Close detail photograph',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    reset: 'Reset view',
    viewport: 'Zoomable crack photograph',
    zoom: 'Zoom level',
    loading: 'Loading detail photograph…',
    error: 'The detail photograph could not be loaded.',
    retry: 'Try again',
  },
};

function CrackDetail({ locale }: { locale: PublicLocale }) {
  const text = copy[locale];
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const { containerRef, handlers, zoom, panOffset, isDragging, zoomIn, zoomOut, reset, setPanOffset, clampPan } = useZoomPan({ maxZoom: 5 });
  const ready = loaded && !failed;
  const handleKey: React.KeyboardEventHandler<HTMLDivElement> = event => {
    if (!ready) return;
    const distance = 5 / zoom;
    switch (event.key) {
      case '+': case '=': zoomIn(); break;
      case '-': zoomOut(); break;
      case '0': reset(); break;
      case 'ArrowLeft': setPanOffset(clampPan(panOffset.x + distance, panOffset.y, zoom)); break;
      case 'ArrowRight': setPanOffset(clampPan(panOffset.x - distance, panOffset.y, zoom)); break;
      case 'ArrowUp': setPanOffset(clampPan(panOffset.x, panOffset.y + distance, zoom)); break;
      case 'ArrowDown': setPanOffset(clampPan(panOffset.x, panOffset.y - distance, zoom)); break;
      default: return;
    }
    event.preventDefault();
  };
  return <>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
      <Button disabled={!ready || zoom >= 5} onClick={zoomIn}>{text.zoomIn}</Button>
      <Button disabled={!ready || zoom <= 1} onClick={zoomOut}>{text.zoomOut}</Button>
      <Button disabled={!ready} onClick={reset}>{text.reset}</Button>
      <Typography role="status" aria-label={text.zoom} sx={{ fontSize: 13 }}>{Math.round(zoom * 100)}%</Typography>
    </Box>
    {failed && <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={() => { reset(); setLoaded(false); setFailed(false); }}>{text.retry}</Button>}>{text.error}</Alert>}
    {!loaded && !failed && <Typography role="status" sx={{ fontSize: 13, mb: 1 }}>{text.loading}</Typography>}
    <Box ref={containerRef} {...(ready ? handlers : {})} role="region" aria-label={text.viewport} tabIndex={0} onKeyDown={handleKey}
      sx={{
        width: '100%', maxWidth: '97.5vh', aspectRatio: '3 / 2', mx: 'auto', overflow: 'hidden',
        bgcolor: colors.graphite, touchAction: 'none', userSelect: 'none',
        cursor: isDragging ? 'grabbing' : zoom > 1 ? 'grab' : 'default',
        '&:focus-visible': { outline: `3px solid ${colors.rust}`, outlineOffset: 3 },
      }}>
      {!failed && <Box component="img" src={articleCrackImages.detail} alt={text.detailAlt} width={2400} height={1600} draggable={false}
        onLoad={() => setLoaded(true)} onError={() => { setFailed(true); setLoaded(false); }}
        sx={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none', transformOrigin: 'center', transform: `scale(${zoom}) translate(${panOffset.x}%, ${panOffset.y}%)` }} />}
    </Box>
  </>;
}

export function CrackExampleFigure({ locale = 'sv' }: { locale?: PublicLocale }) {
  const text = copy[locale];
  const [open, setOpen] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const id = useId();
  return <Box component="figure" sx={{ m: 0, my: 5 }}>
    {previewFailed && <Alert severity="warning" sx={{ mb: 2 }}>{text.previewError}</Alert>}
    <ButtonBase onClick={() => setOpen(true)} aria-label={text.open} aria-haspopup="dialog"
      sx={{
        display: 'block', width: '100%', textAlign: 'left', border: `1px solid ${colors.line}`,
        '&:focus-visible': { outline: `3px solid ${colors.rust}`, outlineOffset: 3 },
        '& img': { display: 'block', width: '100%', height: 'auto' },
      }}>
      {!previewFailed && <img src={articleCrackImages.preview} alt={text.previewAlt} width={1517} height={832} loading="eager"
        onError={() => setPreviewFailed(true)} />}
      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: colors.soft, color: colors.ink, fontSize: 14, fontWeight: 600 }}>
        <ZoomInIcon fontSize="small" />{text.open}
      </Box>
    </ButtonBase>
    <Typography component="figcaption" sx={{ fontSize: 12, mt: 1.5, color: colors.muted }}>{text.caption}</Typography>
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="lg" aria-labelledby={`${id}-title`} aria-describedby={`${id}-instructions`}
      PaperProps={{ sx: { m: { xs: 1, sm: 4 }, width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' }, maxHeight: 'calc(100% - 16px)' } }}>
      <DialogTitle id={`${id}-title`} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: 18, sm: 22 } }}>
        {text.title}<IconButton aria-label={text.close} onClick={() => setOpen(false)} sx={{ ml: 'auto' }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
        <Typography id={`${id}-instructions`} sx={{ color: colors.muted, fontSize: 13, mb: 1 }}>{text.instructions}</Typography>
        {open && <CrackDetail locale={locale} />}
      </DialogContent>
    </Dialog>
  </Box>;
}
